import { parseBuffer } from 'music-metadata';

export interface AudioMetadata {
  bpm?: number; bpmIsIntegerTag?: boolean; key?: string; sampleRate?: number; channels?: number;
  duration?: number; encoderDelay?: number; encoderPadding?: number; originalFrames?: number;
  warning?: string;
}
export function validBpm(value: unknown): number | undefined {
  const bpm = typeof value === 'number' ? value : typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : NaN;
  return Number.isFinite(bpm) && bpm >= 20 && bpm <= 400 ? bpm : undefined;
}
export async function readAudioMetadata(bytes: ArrayBuffer, name: string): Promise<AudioMetadata> {
  try {
    const parsed = await parseBuffer(new Uint8Array(bytes), { path: name, size: bytes.byteLength }, { skipCovers: true, duration: false });
    const tags = Object.values(parsed.native).flat();
    // Text tags retain decimal tempos; MP4's standard tmpo tag is integer-only.
    const textBpm = tags.find(tag => /(?:^|:)(?:BPM|TEMPO|TBPM)$/i.test(tag.id));
    const bpm = validBpm(textBpm?.value) ?? validBpm(parsed.common.bpm);
    const gapless = tags.find(tag => /iTunSMPB$/i.test(tag.id));
    const words = typeof gapless?.value === 'string' ? gapless.value.trim().split(/\s+/) : [];
    const gap = words.length >= 4 && words.slice(0,4).every(v => /^[\da-f]+$/i.test(v)) ? words.slice(1,4).map(v => Number.parseInt(v,16)) : [];
    return { bpm, bpmIsIntegerTag: !textBpm && tags.some(tag=>tag.id==='tmpo'), key: parsed.common.key, sampleRate: parsed.format.sampleRate, channels: parsed.format.numberOfChannels,
      duration: parsed.format.duration, encoderDelay: gap[0], encoderPadding: gap[1], originalFrames: gap[2] };
  } catch {
    return { warning: 'Metadata could not be read. Audio decoding will still be attempted.' };
  }
}

export function resolveBpm(values: (number | undefined)[]): { bpm?: number; conflict: boolean } {
  const known = values.filter((value): value is number => validBpm(value) !== undefined);
  const conflict = known.some(value => Math.abs(value - known[0]) > .01);
  return { bpm: conflict ? undefined : known[0], conflict };
}
